// imports 
import { useEffect, useState } from 'react'
import { EventType } from '@azure/msal-browser'
import { Button, ActivityIndicator } from '@bosch/react-frok'
import { useTranslation } from 'react-i18next'
import { AuthorisationStates } from '@/constants'

// components
import Copy from '@/ui/components/Copy/Copy'

// utils
import { useAuthentication } from '@/hooks/useAuthentication'
import { useNavigation } from '@/hooks/useNavigation'
import useGenericStore from '@/hooks/useGenericStore'

// styles
import './Login.scss'
import { getAssistantIllustration } from '@/hooks/useAssistantIllustration'

const authenticationEventTypes: readonly unknown[] = Object.values(EventType)

const isEventType = (value: unknown): value is EventType =>
    authenticationEventTypes.includes(value)

const isAuthenticationEvent = (value: unknown): value is { eventType: EventType } =>
    typeof value === 'object'
    && value !== null
    && 'eventType' in value
    && isEventType(value.eventType)

const Login = () => {
    const [redirectUrl, setRedirectUrl] = useState('/')

    const { authorisationFlow, loginUser, authenticationState } = useAuthentication()
    const { t } = useTranslation()
    const { navigateTo } = useNavigation()
    const { getGenericStoreValue, removeGenericStoreValue } = useGenericStore()

    const isInProgress =
        authorisationFlow === AuthorisationStates.AUTHORISING ||
        !!sessionStorage.getItem('isAuthorizationInProgress')

    useEffect(() => {
        if (!isAuthenticationEvent(authenticationState)) return

        switch (authenticationState.eventType) {
            case EventType.LOGIN_FAILURE:
            case EventType.ACQUIRE_TOKEN_FAILURE:
            case EventType.SSO_SILENT_FAILURE:
                navigateTo('/identity/unexpectedAuthenticationError', {
                    state: authenticationState,
                })
                break
        }
        // the page leaves when the authentication state changes, not when the navigation function does
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticationState])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const passedRedirectUrl = params.get('redirectUrl')
        if (passedRedirectUrl) setRedirectUrl(passedRedirectUrl)

        if (sessionStorage.getItem('isAuthorizationInProgress')) return
        if (!getGenericStoreValue('disableAutoAuthorize')) loginUser()
        // the login starts once, when the page opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (authorisationFlow === AuthorisationStates.AUTHORISED) navigateTo(redirectUrl)
        // the page leaves when the flow is authorised, not when the redirect url or the navigation function change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authorisationFlow])

    const handleLoginClick = () => {
        if (typeof removeGenericStoreValue === 'function') {
            removeGenericStoreValue('disableAutoAuthorize', true)
        }
        loginUser()
    }

    return (
        <div className="login-page">
            <div className="login-content">
                {!isInProgress && (
                    <>
                        <img
                            className="robot-icon"
                            src={getAssistantIllustration()}
                            alt="robot icon"
                        />
                        <div className="login-description">
                            <Copy headline={t('login:title')} headlineSize={3} alignment="center">
                                <p>{t('login:text')}</p>
                            </Copy>
                            <div className="login-button-wrapper">
                                <div className="artificial-intelligence-disclaimer">
                                    <div className="disclaimer-child disclaimer-text">
                                        {t('general:artificialIntelligenceDisclaimer')}
                                    </div>
                                </div>
                                <Button label={t('login:loginLabel')} onClick={handleLoginClick} />
                            </div>
                        </div>
                    </>
                )}
                {isInProgress && <ActivityIndicator size="small" />}
            </div>
        </div>
    )
}

export default Login
